import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { PlusCircleIcon } from '../constants';
import MqttConfigModal from './MqttConfigModal';
import CalibrationSimulator from './CalibrationSimulator';
import SimulationControlPanel from './SimulationControlPanel';
import SimulationConfigTab from './SimulationConfigTab';
import { io, Socket } from 'socket.io-client';
import { getValidStationsForConfig, getStationRangeName } from '../utils/station-config';

interface SimulatedHardware {
  id: string;
  hardware_uuid: string;
  node_id: number;
  node_type: number;
  line_number: number;
  station_config: number;
  device_name: string;
  device_type: string;
  description?: string;
  firmware_version: string;
  hardware_model: string;
  is_powered_on: boolean;
  process_status: string;
  last_started_at?: string;
  last_stopped_at?: string;
  config_overrides?: any;
  enable_fault_injection: boolean;
  messages_sent: number;
  uptime_seconds: number;
  last_error_message?: string;
  last_error_timestamp?: string;
  test_scenario?: string;
  created_by: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
  line_production_status?: string;  // Add missing field
  connectivity_status?: string;     // Add missing field
  // Machine states for Infeed hardware
  infeed_conveyor_state?: string;
  buffer_hopper_state?: string;
  weigh_hopper_state?: string;
  estop_state?: string;
  station_product_sensors?: { [key: string]: string }; // Station number -> 'ON' | 'OFF'
  // Station arm states for Infeed hardware
  station_1_arm?: string;
  station_2_arm?: string;
  station_3_arm?: string;
  station_4_arm?: string;
  station_5_arm?: string;
  station_6_arm?: string;
  station_7_arm?: string;
  station_8_arm?: string;
  station_9_arm?: string;
  station_10_arm?: string;
  // Machine states for Outfeed hardware
  station_active_button?: string;
  station_red_light?: string;
  station_yellow_light?: string;
  station_green_light?: string;
  station_pocket_door_1?: string;
  station_pocket_door_2?: string;
  station_pocket_door_3?: string;
  station_pocket_door_4?: string;
  station_weigh_hopper_door?: string;
  // Weight scale configuration
  scale_min_weight?: number;
  scale_max_weight?: number;
  scale_variance?: number;
  scale_update_interval?: number;
  // Call for Infeed Product
  call_for_infeed_button?: string;
  call_for_infeed_blue_light?: string;
  // Infeed Calibration Lights and Button
  infeed_red_light?: string;
  infeed_yellow_light?: string;
  infeed_green_light?: string;
  infeed_calibration_button?: string;
  // Manual weight control
  manual_weight_control?: boolean;
  manual_weight_lbs?: number;
  manual_adc_value?: number;
  // Per-station manual weight control
  [key: `station_${number}_manual_weight_lbs`]: number | undefined;
  [key: `station_${number}_manual_adc_value`]: number | undefined;
}

const HardwareSimulation: React.FC = () => {
  const { apiUrl } = useDatabaseContext();
  const [devices, setDevices] = useState<SimulatedHardware[]>([]);
  const [selectedLineNumber, setSelectedLineNumber] = useState<number>(1); // Selected line tab
  const [activeTab, setActiveTab] = useState<'basic' | 'machine' | 'logging' | 'stations'>('basic');
  const [activeStationTab, setActiveStationTab] = useState<number>(1); // For station subtabs
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<SimulatedHardware | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [mqttData, setMqttData] = useState<{ [key: string]: any }>({});
  const [autoRefresh, setAutoRefresh] = useState(false); // Default to OFF
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configDevice, setConfigDevice] = useState<SimulatedHardware | null>(null);
  const [stationAssignments, setStationAssignments] = useState<any[]>([]);
  
  // Test log generation
  const generateTestLog = async (device: SimulatedHardware, level: string, type: string) => {
    try {
      const response = await fetch(`${apiUrl}/mqtt/test-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hardware_uuid: device.hardware_uuid,
          log_level: level,
          log_type: type
        })
      });
      
      if (response.ok) {
        console.log(`Test ${level} log sent via MQTT for ${device.device_name}`);
      }
    } catch (error) {
      console.error('Error sending test log via MQTT:', error);
    }
  };
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const fetchDevicesRef = useRef<() => void>();

  // Form state for add/edit modal
  const [formData, setFormData] = useState({
    node_id: 0,
    node_type: 0,
    line_number: 0,
    station_config: 0,
    description: '',
    enable_fault_injection: false,
    test_scenario: '',
    // Machine states for Infeed hardware
    infeed_conveyor_state: 'OFF',
    buffer_hopper_state: 'CLOSED',
    weigh_hopper_state: 'CLOSED',
    estop_state: 'OFF',
    station_product_sensors: {} as { [key: string]: string },
    // Station arm states for Infeed hardware  
    station_1_arm: 'CLOSED',
    station_2_arm: 'CLOSED',
    station_3_arm: 'CLOSED',
    station_4_arm: 'CLOSED',
    station_5_arm: 'CLOSED',
    station_6_arm: 'CLOSED',
    station_7_arm: 'CLOSED',
    station_8_arm: 'CLOSED',
    station_9_arm: 'CLOSED',
    station_10_arm: 'CLOSED',
    // Machine states for Outfeed hardware
    station_active_button: 'OFF',
    station_red_light: 'OFF',
    station_yellow_light: 'OFF',
    station_green_light: 'OFF',
    station_pocket_door_1: 'OFF',
    station_pocket_door_2: 'OFF',
    station_pocket_door_3: 'OFF',
    station_pocket_door_4: 'OFF',
    station_weigh_hopper_door: 'OFF',
    // Weight scale configuration
    scale_min_weight: 0,
    scale_max_weight: 60,
    scale_variance: 5,
    scale_update_interval: 1,
    // Call for Infeed Product
    call_for_infeed_button: 'OFF',
    call_for_infeed_blue_light: 'OFF',
    // Infeed Calibration Lights and Button
    infeed_red_light: 'OFF',
    infeed_yellow_light: 'OFF',
    infeed_green_light: 'OFF',
    infeed_calibration_button: 'OFF',
    // Manual weight control
    manual_weight_control: false,
    manual_weight_lbs: 0,
    manual_adc_value: 8388608
  });

  // Fetch all simulated hardware devices
  const fetchDevices = useCallback(async (isInitialLoad = false) => {
    console.log('[fetchDevices] Called at', new Date().toISOString(), 'Initial:', isInitialLoad);
    try {
      // Only show loading on initial load
      if (isInitialLoad) {
        setLoading(true);
      }
      console.log('[fetchDevices] Fetching from:', `${apiUrl}/simulated-hardware`);
      const response = await fetch(`${apiUrl}/simulated-hardware`);
      if (response.ok) {
        const result = await response.json();
        console.log('[fetchDevices] API response:', result);
        // The API returns data.devices which is the array
        const devicesData = result.data?.devices || [];
        const newDevices = Array.isArray(devicesData) ? devicesData : [];
        console.log('[fetchDevices] Setting devices:', newDevices.length, 'devices');
        console.log('[fetchDevices] First device status:', newDevices[0]?.line_production_status);
        
        // Force a complete state replacement
        setDevices(() => [...newDevices]);
      } else {
        console.error('[fetchDevices] Response not OK:', response.status);
      }
    } catch (error) {
      console.error('[fetchDevices] Error fetching hardware devices:', error);
      if (isInitialLoad) {
        setDevices([]); // Set to empty array on error
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, [apiUrl]);

  // Store fetchDevices in ref so WebSocket can call it without causing re-renders
  useEffect(() => {
    fetchDevicesRef.current = fetchDevices;
  }, [fetchDevices]);

  useEffect(() => {
    fetchDevices(true); // Pass true for initial load
    
    // Connect to WebSocket for real-time updates
    // Remove /api from the URL for Socket.IO connection
    const socketUrl = apiUrl.replace('/api', '');
    console.log('[Hardware Simulation] Initializing WebSocket connection to:', socketUrl);
    socketRef.current = io(socketUrl, {
      transports: ['websocket', 'polling'],  // Allow fallback to polling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    
    socketRef.current.on('connect', () => {
      console.log('[Hardware Simulation] WebSocket connected');
      setIsConnected(true);
    });
    
    socketRef.current.on('disconnect', () => {
      console.log('[Hardware Simulation] WebSocket disconnected');
      setIsConnected(false);
    });
    
    // Listen for hardware device updates
    socketRef.current.on('hardware-update', (update: any) => {
      console.log('[Hardware Simulation] 🔔 Received hardware update:', update);
      
      // If the update includes state data, update the local state immediately
      if (update.states && update.hardwareUUID) {
        console.log('[Hardware Simulation] Applying WebSocket state update directly for', update.hardwareUUID);
        
        // Update the device in local state immediately with WebSocket data
        setDevices(prevDevices => {
          return prevDevices.map(device => {
            if (device.hardware_uuid === update.hardwareUUID) {
              // Apply the state updates from WebSocket
              const updatedDevice = { ...device };
              
              // Map the states from WebSocket to device properties
              if (update.states.buffer_hopper_state !== undefined) {
                updatedDevice.buffer_hopper_state = update.states.buffer_hopper_state;
              }
              if (update.states.weigh_hopper_state !== undefined) {
                updatedDevice.weigh_hopper_state = update.states.weigh_hopper_state;
              }
              if (update.states.infeed_conveyor_state !== undefined) {
                updatedDevice.infeed_conveyor_state = update.states.infeed_conveyor_state;
              }
              if (update.states.red_light !== undefined) {
                updatedDevice.infeed_red_light = update.states.red_light;
              }
              if (update.states.yellow_light !== undefined) {
                updatedDevice.infeed_yellow_light = update.states.yellow_light;
              }
              if (update.states.green_light !== undefined) {
                updatedDevice.infeed_green_light = update.states.green_light;
              }
              if (update.states.line_production_status !== undefined) {
                updatedDevice.line_production_status = update.states.line_production_status;
              }
              
              console.log('[Hardware Simulation] Updated device state locally:', updatedDevice.hardware_uuid, {
                buffer_hopper: updatedDevice.buffer_hopper_state,
                lights: `${updatedDevice.infeed_red_light}/${updatedDevice.infeed_yellow_light}/${updatedDevice.infeed_green_light}`
              });
              
              return updatedDevice;
            }
            return device;
          });
        });
        
        // Still fetch from database after a short delay to ensure consistency
        // but the UI will update immediately with WebSocket data
        setTimeout(() => {
          if (fetchDevicesRef.current) {
            fetchDevicesRef.current();
          }
        }, 1000);
      } else {
        // No state data in update, fall back to database fetch
        console.log('[Hardware Simulation] No state data in update, fetching from database...');
        if (fetchDevicesRef.current) {
          fetchDevicesRef.current();
        }
      }
      
      console.log('[Hardware Simulation] Update processing complete at', new Date().toISOString());
    });
    
    // Listen for mode changes (including calibration mode changes)
    socketRef.current.on('mode-changed', (update: any) => {
      console.log('[Hardware Simulation] 🔔 Received mode change:', update);
      console.log('[Hardware Simulation] Mode changed to:', update.newMode, 'for line:', update.lineId);
      // Force immediate refresh when mode changes
      if (fetchDevicesRef.current) {
        fetchDevicesRef.current();
      }
    });
    
    // Listen for calibration state changes
    socketRef.current.on('calibration-state-change', (update: any) => {
      console.log('[Hardware Simulation] 🔔 Received calibration state change:', update);
      // Force immediate refresh when calibration state changes
      if (fetchDevicesRef.current) {
        fetchDevicesRef.current();
      }
    });
    
    // Listen for IO state updates that might affect hardware status
    socketRef.current.on('io-state-update', (update: any) => {
      console.log('[Hardware Simulation] Received IO state update:', update);
      // Update MQTT data for the specific device
      setMqttData(prevData => ({
        ...prevData,
        [update.hardwareUUID]: update.metrics
      }));
    });
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [apiUrl]); // Only depend on apiUrl, not fetchDevices
  
  useEffect(() => {
    // Set up fallback polling if autoRefresh is enabled (in case WebSocket fails)
    if (autoRefresh) {
      const interval = setInterval(() => {
        if (!isConnected) {
          // Only poll if WebSocket is disconnected
          fetchDevices();
        }
      }, 5000); // Fallback polling every 5 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh, isConnected]);

  // Handle power toggle
  const handlePowerToggle = async (device: SimulatedHardware) => {
    try {
      const newPowerState = !device.is_powered_on;
      const response = await fetch(`${apiUrl}/simulated-hardware/${device.hardware_uuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_powered_on: newPowerState })
      });

      if (response.ok) {
        await fetchDevices(); // Refresh list
      } else {
        const error = await response.json();
        alert(`Power toggle failed: ${error.error}`);
      }
    } catch (error) {
      console.error('Error toggling power:', error);
      alert('Network error: Failed to toggle power');
    }
  };

  // Fetch station assignments for a line
  const fetchStationAssignments = async (lineNumber: number) => {
    try {
      // Fetch hardware-reported station status via MQTT (what the hardware actually knows)
      const response = await fetch(`${apiUrl}/hardware-station-status/${lineNumber}`);
      if (response.ok) {
        const result = await response.json();

        if (result.data) {
          // Convert active stations to assignment-like format for display
          const mockAssignments = result.data.activeStations.map((stationNum: number) => ({
            stationNumber: stationNum,
            isActive: true,
            employeeId: null,
            employeeName: null // Hardware doesn't know employee names
          }));

          // Store the full hardware status for debugging
          console.log('Hardware Station Status:', {
            lineNumber: result.data.lineNumber,
            hardwareUuid: result.data.hardwareUuid,
            activeStations: result.data.activeStations,
            stationCount: result.data.stationCount,
            lastUpdate: result.data.lastUpdate,
            lastSeen: result.data.lastSeen
          });

          setStationAssignments(mockAssignments);
        }
      }
    } catch (error) {
      console.error('Error fetching hardware station status:', error);
    }
  };

  // Handle device deletion
  const handleDeleteDevice = async (device: SimulatedHardware) => {
    if (!confirm(`Delete hardware device "${device.device_name}"?\n\nThis will permanently remove the device and stop any running simulation.`)) {
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/simulated-hardware/${device.hardware_uuid}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchDevices(); // Refresh list
      } else {
        const error = await response.json();
        alert(`Delete failed: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting device:', error);
      alert('Network error: Failed to delete device');
    }
  };

  // Handle add/edit device
  const handleSaveDevice = async () => {
    try {
      if (isEditing && selectedDevice) {
        // Update existing device
        console.log('Sending formData:', formData);
        console.log('Station 2 values being sent:', {
          station_2_active_button: formData.station_2_active_button,
          station_2_red_light: formData.station_2_red_light,
          station_2_scale_min_weight: formData.station_2_scale_min_weight,
          station_2_scale_max_weight: formData.station_2_scale_max_weight
        });
        const response = await fetch(`${apiUrl}/simulated-hardware/${selectedDevice.hardware_uuid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          await fetchDevices();
          handleCloseModal();
        } else {
          const error = await response.json();
          alert(`Update failed: ${error.error}`);
        }
      } else {
        // Create new device
        const response = await fetch(`${apiUrl}/simulated-hardware`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          await fetchDevices();
          handleCloseModal();
        } else {
          const error = await response.json();
          alert(`Create failed: ${error.error}`);
        }
      }
    } catch (error) {
      console.error('Error saving device:', error);
      alert('Network error: Failed to save device');
    }
  };

  // View configuration handler
  const handleViewConfig = (device: SimulatedHardware) => {
    setConfigDevice(device);
    setShowConfigModal(true);
  };

  // Modal handlers
  const handleOpenAddModal = () => {
    setFormData({
      node_id: 0,
      node_type: 0,
      line_number: 1,
      station_config: 1,
      description: '',
      enable_fault_injection: false,
      test_scenario: '',
      infeed_conveyor_state: 'OFF',
      buffer_hopper_state: 'CLOSED',
      weigh_hopper_state: 'CLOSED',
      estop_state: 'OFF',
      station_product_sensors: {},
      station_1_arm: 'CLOSED',
      station_2_arm: 'CLOSED',
      station_3_arm: 'CLOSED',
      station_4_arm: 'CLOSED',
      station_5_arm: 'CLOSED',
      station_6_arm: 'CLOSED',
      station_7_arm: 'CLOSED',
      station_8_arm: 'CLOSED',
      station_9_arm: 'CLOSED',
      station_10_arm: 'CLOSED',
      station_active_button: 'OFF',
      station_red_light: 'OFF',
      station_yellow_light: 'OFF',
      station_green_light: 'OFF',
      station_pocket_door_1: 'OFF',
      station_pocket_door_2: 'OFF',
      station_pocket_door_3: 'OFF',
      station_pocket_door_4: 'OFF',
      station_weigh_hopper_door: 'OFF',
      scale_min_weight: 0,
      scale_max_weight: 60,
      scale_variance: 5,
      scale_update_interval: 1,
      call_for_infeed_button: 'OFF',
      call_for_infeed_blue_light: 'OFF',
      manual_weight_control: false,
      manual_weight_lbs: 0,
      manual_adc_value: 8388608,
      scale_zero_adc: 8388608,
      scale_calibration_adc: 16777215,
      scale_calibration_state: 'GOOD',
      scale_excitation_voltage: 5.0,
      scale_excitation_current: 20.0
    });
    setSelectedDevice(null);
    setIsEditing(false);
    setActiveTab('basic');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = async (device: SimulatedHardware) => {
    // Fetch station assignments if this is an infeed device
    if (device.node_type === 1) {
      await fetchStationAssignments(device.line_number);
    }

    const formDataObj: any = {
      node_id: device.node_id,
      node_type: device.node_type,
      line_number: device.line_number,
      station_config: device.station_config,
      description: device.description || '',
      enable_fault_injection: device.enable_fault_injection,
      test_scenario: device.test_scenario || '',
      infeed_conveyor_state: device.infeed_conveyor_state || 'OFF',
      buffer_hopper_state: device.buffer_hopper_state || 'CLOSED',
      weigh_hopper_state: device.weigh_hopper_state || 'CLOSED',
      estop_state: device.estop_state || 'OFF',
      station_product_sensors: device.station_product_sensors || {},
      station_1_arm: device.station_1_arm || 'CLOSED',
      station_2_arm: device.station_2_arm || 'CLOSED',
      station_3_arm: device.station_3_arm || 'CLOSED',
      station_4_arm: device.station_4_arm || 'CLOSED',
      station_5_arm: device.station_5_arm || 'CLOSED',
      station_6_arm: device.station_6_arm || 'CLOSED',
      station_7_arm: device.station_7_arm || 'CLOSED',
      station_8_arm: device.station_8_arm || 'CLOSED',
      station_9_arm: device.station_9_arm || 'CLOSED',
      station_10_arm: device.station_10_arm || 'CLOSED',
      station_active_button: device.station_active_button || 'OFF',
      station_red_light: device.station_red_light || 'OFF',
      station_yellow_light: device.station_yellow_light || 'OFF',
      station_green_light: device.station_green_light || 'OFF',
      station_pocket_door_1: device.station_pocket_door_1 || 'OFF',
      station_pocket_door_2: device.station_pocket_door_2 || 'OFF',
      station_pocket_door_3: device.station_pocket_door_3 || 'OFF',
      station_pocket_door_4: device.station_pocket_door_4 || 'OFF',
      station_weigh_hopper_door: device.station_weigh_hopper_door || 'OFF',
      scale_min_weight: device.scale_min_weight || 0,
      scale_max_weight: device.scale_max_weight || 60,
      scale_variance: device.scale_variance || 5,
      scale_update_interval: device.scale_update_interval || 1,
      call_for_infeed_button: device.call_for_infeed_button || 'OFF',
      call_for_infeed_blue_light: device.call_for_infeed_blue_light || 'OFF',
      manual_weight_control: device.manual_weight_control || false,
      manual_weight_lbs: device.manual_weight_lbs || 0,
      manual_adc_value: device.manual_adc_value || 8388608,
      scale_zero_adc: device.scale_zero_adc || 8388608,
      scale_calibration_adc: device.scale_calibration_adc || 16777215,
      scale_calibration_state: device.scale_calibration_state || 'GOOD',
      scale_excitation_voltage: device.scale_excitation_voltage || 5.0,
      scale_excitation_current: device.scale_excitation_current || 20.0
    };
    
    // Load per-station configuration for all 10 stations
    for (let station = 1; station <= 10; station++) {
      // Per-station machine states
      formDataObj[`station_${station}_active_button`] = device[`station_${station}_active_button`] || 'OFF';
      formDataObj[`station_${station}_red_light`] = device[`station_${station}_red_light`] || 'OFF';
      formDataObj[`station_${station}_yellow_light`] = device[`station_${station}_yellow_light`] || 'OFF';
      formDataObj[`station_${station}_green_light`] = device[`station_${station}_green_light`] || 'OFF';
      formDataObj[`station_${station}_pocket_door_1`] = device[`station_${station}_pocket_door_1`] || 'OFF';
      formDataObj[`station_${station}_pocket_door_2`] = device[`station_${station}_pocket_door_2`] || 'OFF';
      formDataObj[`station_${station}_pocket_door_3`] = device[`station_${station}_pocket_door_3`] || 'OFF';
      formDataObj[`station_${station}_pocket_door_4`] = device[`station_${station}_pocket_door_4`] || 'OFF';
      formDataObj[`station_${station}_weigh_hopper_door`] = device[`station_${station}_weigh_hopper_door`] || 'OFF';
      formDataObj[`station_${station}_call_for_infeed_button`] = device[`station_${station}_call_for_infeed_button`] || 'OFF';
      formDataObj[`station_${station}_call_for_infeed_blue_light`] = device[`station_${station}_call_for_infeed_blue_light`] || 'OFF';
      
      // Per-station weight scale configuration
      formDataObj[`station_${station}_scale_min_weight`] = device[`station_${station}_scale_min_weight`] || 0;
      formDataObj[`station_${station}_scale_max_weight`] = device[`station_${station}_scale_max_weight`] || 60;
      formDataObj[`station_${station}_scale_variance`] = device[`station_${station}_scale_variance`] || 5;
      formDataObj[`station_${station}_scale_update_interval`] = device[`station_${station}_scale_update_interval`] || 1;
      
      // Per-station manual weight control
      formDataObj[`station_${station}_manual_weight_lbs`] = device[`station_${station}_manual_weight_lbs`] || 0;
      formDataObj[`station_${station}_manual_adc_value`] = device[`station_${station}_manual_adc_value`] || 8388608;
    }
    
    setFormData(formDataObj);
    setSelectedDevice(device);
    setIsEditing(true);
    setActiveTab('basic');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDevice(null);
    setIsEditing(false);
  };

  // Get device type label
  const getDeviceTypeLabel = (nodeType: number) => {
    switch (nodeType) {
      case 0: return 'Unconfigured';
      case 1: return 'Infeed';
      case 2: return 'Outfeed';
      case 3: return 'QC';
      default: return 'Unknown';
    }
  };

  // Get power status styling
  const getPowerStatusStyle = (isPoweredOn: boolean, processStatus: string) => {
    if (!isPoweredOn) {
      return 'bg-gray-100 text-gray-600 border-gray-300';
    }
    
    switch (processStatus) {
      case 'RUNNING':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'STOPPED':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'ERROR':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'STARTING':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  // Track calibration states here in the parent which stays mounted
  // This useState MUST come before useMemo to maintain hooks order
  const [calibrationStates, setCalibrationStates] = useState<{[key: string]: string}>({});
  
  // Get unique line numbers for tabs
  const lineNumbers = [...new Set(devices.map(d => d.line_number))].sort((a, b) => a - b);
  
  // Memoize the line ID to prevent recreating CalibrationSimulator
  // MUST be called before any conditional returns to maintain hooks order
  const lineIdForCalibration = useMemo(() => {
    const devicesForLine = devices.filter(d => d.line_number === selectedLineNumber);
    return devicesForLine.length > 0 ? (devicesForLine[0].actual_line_id || '') : '';
  }, [devices, selectedLineNumber]);
  
  // Filter devices for selected line
  const lineDevices = devices.filter(d => d.line_number === selectedLineNumber);
  
  // Poll for calibration states in the parent component
  // MUST be called before any conditional returns to maintain hooks order
  useEffect(() => {
    if (!lineIdForCalibration) return;
    
    const checkCalibrationStates = async () => {
      try {
        const ioResponse = await fetch(`${apiUrl}/io-system-states`);
        if (ioResponse.ok) {
          const ioData = await ioResponse.json();
          const lineStates = ioData.io_states?.[lineIdForCalibration];
          
          if (lineStates?.outfeed?.devices) {
            const states: {[key: string]: string} = {};
            
            // Process ALL active devices (not just the first one)
            const activeDevices = lineStates.outfeed.devices.filter((d: any) => d.mqtt_status === 'RECEIVING');
            
            // Process each active device to get all station states
            activeDevices.forEach((device: any) => {
              if (device?.station_states) {
                Object.entries(device.station_states).forEach(([stationKey, stationData]: [string, any]) => {
                  const status = stationData?.status || 'STOP';
                  
                  if (status === 'CALIBRATION-COMPLETE') {
                    states[`station_${stationKey}`] = 'calibrated';
                  } else if (status === 'CALIBRATION-ZERO-SET') {
                    states[`station_${stationKey}`] = 'zero_set';
                  } else if (status === 'CALIBRATION-ACTIVE') {
                    states[`station_${stationKey}`] = 'waiting';
                  } else {
                    states[`station_${stationKey}`] = 'ready';
                  }
                });
              }
            });
            
            // Check infeed status
            if (lineStates.infeed?.operational_status?.includes('CALIBRATION')) {
              const infeedStatus = lineStates.infeed.operational_status;
              if (infeedStatus === 'CALIBRATION-COMPLETE') {
                states.infeed = 'calibrated';
              } else if (infeedStatus === 'CALIBRATION-ZERO-SET') {
                states.infeed = 'zero_set';
              } else {
                states.infeed = 'waiting';
              }
            } else {
              // Check database session for infeed
              const sessionResponse = await fetch(`${apiUrl}/calibration/session/${lineIdForCalibration}`);
              if (sessionResponse.ok) {
                const sessionData = await sessionResponse.json();
                if (sessionData.data && sessionData.data.mode === 'CALIBRATING') {
                  // There's an active calibration session
                  if (sessionData.data.calibration_data?.infeed) {
                    const infeedData = sessionData.data.calibration_data.infeed;
                    if (infeedData.calibrated) {
                      states.infeed = 'calibrated';
                    } else if (infeedData.zeroSet) {
                      states.infeed = 'zero_set';
                    } else {
                      states.infeed = 'waiting';
                    }
                  } else {
                    // Active calibration session but no infeed data yet - waiting for first button press
                    states.infeed = 'waiting';
                  }
                }
                // If no active calibration session, don't set any infeed state
              }
            }
            
            setCalibrationStates(states);
            console.log('HardwareSimulation: Updated calibration states:', states);
          }
        }
      } catch (error) {
        console.error('Error checking calibration states:', error);
      }
    };
    
    // Check immediately and then poll
    checkCalibrationStates();
    const interval = setInterval(checkCalibrationStates, 2000);
    
    return () => clearInterval(interval);
  }, [apiUrl, lineIdForCalibration]);
  
  // Check if selected line is in calibration mode
  // We'll show the calibration simulator if any device on the line reports calibration status
  // This includes individual station calibration states
  const isLineInCalibration = lineDevices.some(d => 
    d.line_production_status?.includes('CALIBRATION') || 
    d.line_production_status?.includes('CALIBRATING')
  );
  console.log('HardwareSimulation: Calibration mode check for line:', selectedLineNumber, 'isCalibrating:', isLineInCalibration);
  
  // Don't show loading state after initial load to prevent full page re-render
  if (loading && devices.length === 0) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-slate-700">Simulated Hardware Devices</h2>
          <p className="text-slate-600 mt-2">
            Create and manage virtual hardware devices that simulate real production equipment via MQTT.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-1">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
            <span className="text-sm text-slate-500">{isConnected ? 'Live' : 'Offline'}</span>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-slate-300"
            />
            Fallback polling
          </label>
          <button
            onClick={() => fetchDevices()}
            className="px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md transition-colors"
          >
            🔄 Refresh
          </button>
          <button
            onClick={handleOpenAddModal}
            className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Add Hardware Device
          </button>
        </div>
      </div>

      {/* Line Tabs */}
      {lineNumbers.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-1 p-2">
              {lineNumbers.map(lineNum => {
                const lineHasCalibration = devices
                  .filter(d => d.line_number === lineNum)
                  .some(d => d.line_production_status?.includes('CALIBRATION'));
                
                return (
                  <button
                    key={lineNum}
                    onClick={() => setSelectedLineNumber(lineNum)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
                      selectedLineNumber === lineNum
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>Line {lineNum}</span>
                    {lineHasCalibration && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedLineNumber === lineNum ? 'bg-white bg-opacity-20 text-white' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        <span className="w-2 h-2 bg-current rounded-full animate-pulse mr-1"></span>
                        Calibrating
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Calibration Control Panel */}

      {/* Simulation Control Panel */}
      <SimulationControlPanel
        selectedLineNumber={selectedLineNumber}
        lineDevices={lineDevices}
      />

      {/* Hardware Devices Table */}
      <div className="bg-white shadow-sm rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-slate-900">Line {selectedLineNumber} Hardware Devices ({lineDevices.length})</h3>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-green-600">🟢 Online: {lineDevices.filter(d => d.is_powered_on && d.connectivity_status === 'online').length}</span>
            <span className="text-yellow-600">🟡 Starting: {lineDevices.filter(d => d.connectivity_status === 'starting').length}</span>
            <span className="text-gray-600">⚫ Offline: {lineDevices.filter(d => !d.is_powered_on).length}</span>
          </div>
        </div>

        {/* Calibration Simulator - Shows when selected line is in calibration mode */}
        {isLineInCalibration && lineIdForCalibration && (
          <CalibrationSimulator 
            lineId={lineIdForCalibration} 
            lineName={`Line ${selectedLineNumber}`}
            lineNumber={selectedLineNumber}
            calibrationStates={calibrationStates}
            key={lineIdForCalibration} // Add key to prevent unnecessary remounts
          />
        )}

        {lineDevices.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-slate-500">No hardware devices configured for Line {selectedLineNumber}</p>
            <button
              onClick={handleOpenAddModal}
              className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Create your first virtual hardware device
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Configuration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Device Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Line Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Statistics
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {lineDevices.map((device, index) => (
                  <tr key={`${device.id}-${device.line_production_status}-${index}`} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-slate-900">{device.device_name}</div>
                        <div className="text-sm text-slate-500">
                          {getDeviceTypeLabel(device.node_type)} • {device.hardware_model}
                        </div>
                        {device.description && (
                          <div className="text-sm text-slate-400 mt-1">{device.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Node ID:</span> {device.node_id}</div>
                        <div><span className="font-medium">Line:</span> {device.line_number}</div>
                        <div><span className="font-medium">Config:</span> {device.station_config}</div>
                        <div className="text-xs text-slate-400 mt-1">
                          UUID: {device.hardware_uuid.slice(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPowerStatusStyle(device.is_powered_on, device.process_status)}`}>
                          {device.is_powered_on ? (
                            <>🟢 {device.process_status}</>
                          ) : (
                            <>🔴 POWERED OFF</>
                          )}
                        </span>
                        {device.enable_fault_injection && (
                          <div className="text-xs text-orange-600">⚠️ Fault Injection Enabled</div>
                        )}
                        {device.test_scenario && (
                          <div className="text-xs text-blue-600">🧪 {device.test_scenario}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                          device.line_production_status === 'PRODUCTION' 
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : device.line_production_status === 'STOP'
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : device.line_production_status === 'MAINTENANCE'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                            : device.line_production_status === 'PREPARE'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : device.line_production_status === 'CLEAN'
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                          {device.line_production_status || 'UNKNOWN'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Messages:</span> {device.messages_sent.toLocaleString()}</div>
                        <div><span className="font-medium">Uptime:</span> {Math.floor(device.uptime_seconds / 60)}m</div>
                        {device.is_powered_on && (
                          <div className="text-xs text-blue-600">
                            📊 Last MQTT: {device.last_started_at ? new Date(device.last_started_at).toLocaleTimeString() : 'N/A'}
                          </div>
                        )}
                        {mqttData[device.hardware_uuid] && (
                          <div className="text-xs text-green-600">
                            📡 Live: {JSON.stringify(mqttData[device.hardware_uuid]).slice(0, 50)}...
                          </div>
                        )}
                        {isConnected && device.is_powered_on && (
                          <div className="text-xs text-green-500">
                            ⚡ Real-time updates active
                          </div>
                        )}
                        {device.last_error_message && (
                          <div className="text-xs text-red-600 mt-1" title={device.last_error_timestamp}>
                            ❌ {device.last_error_message}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewConfig(device)}
                          className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                        >
                          View Config
                        </button>
                        <button
                          onClick={() => handlePowerToggle(device)}
                          className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
                            device.is_powered_on
                              ? 'text-red-700 bg-red-50 border-red-200 hover:bg-red-100'
                              : 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                          }`}
                        >
                          {device.is_powered_on ? 'Power Off' : 'Power On'}
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(device)}
                          className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDevice(device)}
                          className="px-3 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Device Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg shadow-xl ${
            (formData.node_type === 1 || formData.node_type === 2) ? 'max-w-4xl' : 'max-w-md'
          } w-full max-h-[90vh] flex flex-col`}>
            <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <h3 className="text-lg font-medium text-slate-900">
                {isEditing ? 'Edit Hardware Device' : 'Add Hardware Device'}
              </h3>
            </div>

            {/* Tab Navigation for Infeed and Outfeed Devices */}
            {(formData.node_type === 1 || formData.node_type === 2) && (
              <div className="px-6 pt-4 border-b border-slate-200 flex-shrink-0">
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab('basic')}
                    className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'basic'
                        ? 'text-indigo-600 border-indigo-600'
                        : 'text-slate-500 border-transparent hover:text-slate-700'
                    }`}
                  >
                    Basic Configuration
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('machine')}
                    className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'machine'
                        ? 'text-indigo-600 border-indigo-600'
                        : 'text-slate-500 border-transparent hover:text-slate-700'
                    }`}
                  >
                    {formData.node_type === 1 ? 'Infeed Machine States' : 'Outfeed Machine States'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('logging')}
                    className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'logging'
                        ? 'text-indigo-600 border-indigo-600'
                        : 'text-slate-500 border-transparent hover:text-slate-700'
                    }`}
                  >
                    Test Logging
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('simulation')}
                    className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'simulation'
                        ? 'text-indigo-600 border-indigo-600'
                        : 'text-slate-500 border-transparent hover:text-slate-700'
                    }`}
                  >
                    Simulation Config
                  </button>
                  {(formData.node_type === 1 || formData.node_type === '1') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('stations')}
                      className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'stations'
                          ? 'text-indigo-600 border-indigo-600'
                          : 'text-slate-500 border-transparent hover:text-slate-700'
                      }`}
                    >
                      Station Status
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-grow">
              {/* Show basic config for all devices or when on basic tab for Infeed/Outfeed */}
              {((formData.node_type !== 1 && formData.node_type !== 2) || activeTab === 'basic') && (
                <>
              {/* Node Switch Configuration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Node ID (Switch 1)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={formData.node_id}
                    onChange={(e) => setFormData({...formData, node_id: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Device Type (Switch 2)
                  </label>
                  <select
                    value={formData.node_type}
                    onChange={(e) => setFormData({...formData, node_type: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value={0}>Unconfigured</option>
                    <option value={1}>Infeed</option>
                    <option value={2}>Outfeed</option>
                    <option value={3}>QC</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Line Number (Switch 3)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={formData.line_number}
                    onChange={(e) => setFormData({...formData, line_number: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Config (Switch 4)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={formData.station_config}
                    onChange={(e) => setFormData({...formData, station_config: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description for this device..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Test Scenario
                </label>
                <input
                  type="text"
                  value={formData.test_scenario}
                  onChange={(e) => setFormData({...formData, test_scenario: e.target.value})}
                  placeholder="Optional test scenario name..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="fault_injection"
                  checked={formData.enable_fault_injection}
                  onChange={(e) => setFormData({...formData, enable_fault_injection: e.target.checked})}
                  className="h-4 w-4 text-indigo-600 border-slate-300 rounded"
                />
                <label htmlFor="fault_injection" className="ml-2 text-sm text-slate-700">
                  Enable fault injection for testing error scenarios
                </label>
              </div>
              </>
              )}

              {/* Machine States for Infeed Hardware - Only show on Machine States tab */}
              {formData.node_type === 1 && activeTab === 'machine' && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-slate-900">Infeed Machine States</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Infeed Conveyor
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, infeed_conveyor_state: 'OFF'})}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                            formData.infeed_conveyor_state === 'OFF'
                              ? 'text-red-700 bg-red-50 border-red-300 shadow-sm'
                              : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          OFF
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, infeed_conveyor_state: 'ON'})}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                            formData.infeed_conveyor_state === 'ON'
                              ? 'text-green-700 bg-green-50 border-green-300 shadow-sm'
                              : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          ON
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Buffer Hopper State
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, buffer_hopper_state: 'CLOSED'})}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                            formData.buffer_hopper_state === 'CLOSED'
                              ? 'text-red-700 bg-red-50 border-red-300 shadow-sm'
                              : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          CLOSED
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, buffer_hopper_state: 'OPEN'})}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                            formData.buffer_hopper_state === 'OPEN'
                              ? 'text-green-700 bg-green-50 border-green-300 shadow-sm'
                              : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          OPEN
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Weigh Hopper State
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, weigh_hopper_state: 'CLOSED'})}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                            formData.weigh_hopper_state === 'CLOSED'
                              ? 'text-red-700 bg-red-50 border-red-300 shadow-sm'
                              : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          CLOSED
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, weigh_hopper_state: 'OPEN'})}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                            formData.weigh_hopper_state === 'OPEN'
                              ? 'text-green-700 bg-green-50 border-green-300 shadow-sm'
                              : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          OPEN
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        ESTOP
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, estop_state: 'OFF'})}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                            formData.estop_state === 'OFF'
                              ? 'text-green-700 bg-green-50 border-green-300 shadow-sm'
                              : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          OFF
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, estop_state: 'ON'})}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                            formData.estop_state === 'ON'
                              ? 'text-red-700 bg-red-50 border-red-300 shadow-sm'
                              : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          ON
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Station Product Sensors
                      <span className="text-xs text-slate-500 ml-2">(ON/OFF for each station)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div className="grid grid-cols-5 gap-2">
                        {[1, 2, 3, 4, 5].map((stationNum) => (
                          <div key={stationNum} className="border border-slate-200 rounded p-2">
                            <div className="text-xs font-medium text-slate-600 mb-1">Station {stationNum}</div>
                            <div className="flex flex-col space-y-1">
                              <button
                                type="button"
                                onClick={() => {
                                  const newSensors = { ...formData.station_product_sensors };
                                  newSensors[stationNum] = 'OFF';
                                  setFormData({...formData, station_product_sensors: newSensors});
                                }}
                                className={`px-1 py-1 text-xs font-medium rounded border transition-colors ${
                                  (formData.station_product_sensors[stationNum] || 'OFF') === 'OFF'
                                  ? 'text-red-700 bg-red-50 border-red-300 shadow-sm'
                                  : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                                OFF
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const newSensors = { ...formData.station_product_sensors };
                                  newSensors[stationNum] = 'ON';
                                  setFormData({...formData, station_product_sensors: newSensors});
                                }}
                                className={`px-1 py-1 text-xs font-medium rounded border transition-colors ${
                                  (formData.station_product_sensors[stationNum] || 'OFF') === 'ON'
                                    ? 'text-green-700 bg-green-50 border-green-300 shadow-sm'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                ON
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {[6, 7, 8, 9, 10].map((stationNum) => (
                          <div key={stationNum} className="border border-slate-200 rounded p-2">
                            <div className="text-xs font-medium text-slate-600 mb-1">Station {stationNum}</div>
                            <div className="flex flex-col space-y-1">
                              <button
                                type="button"
                                onClick={() => {
                                  const newSensors = { ...formData.station_product_sensors };
                                  newSensors[stationNum] = 'OFF';
                                  setFormData({...formData, station_product_sensors: newSensors});
                                }}
                                className={`px-1 py-1 text-xs font-medium rounded border transition-colors ${
                                  (formData.station_product_sensors[stationNum] || 'OFF') === 'OFF'
                                    ? 'text-red-700 bg-red-50 border-red-300 shadow-sm'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                OFF
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const newSensors = { ...formData.station_product_sensors };
                                  newSensors[stationNum] = 'ON';
                                  setFormData({...formData, station_product_sensors: newSensors});
                                }}
                                className={`px-1 py-1 text-xs font-medium rounded border transition-colors ${
                                  (formData.station_product_sensors[stationNum] || 'OFF') === 'ON'
                                    ? 'text-green-700 bg-green-50 border-green-300 shadow-sm'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                ON
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Station Arms
                      <span className="text-xs text-slate-500 ml-2">(OPEN/CLOSED for each station)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div className="grid grid-cols-5 gap-2">
                        {[1, 2, 3, 4, 5].map((stationNum) => (
                          <div key={stationNum} className="border border-slate-200 rounded p-2">
                            <div className="text-xs font-medium text-slate-600 mb-1">Station {stationNum}</div>
                            <div className="flex flex-col space-y-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({...formData, [`station_${stationNum}_arm`]: 'CLOSED'});
                                }}
                                className={`px-1 py-1 text-xs font-medium rounded border transition-colors ${
                                  (formData[`station_${stationNum}_arm` as keyof typeof formData] || 'CLOSED') === 'CLOSED'
                                    ? 'text-red-700 bg-red-50 border-red-300 shadow-sm'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                CLOSED
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({...formData, [`station_${stationNum}_arm`]: 'OPEN'});
                                }}
                                className={`px-1 py-1 text-xs font-medium rounded border transition-colors ${
                                  (formData[`station_${stationNum}_arm` as keyof typeof formData] || 'CLOSED') === 'OPEN'
                                    ? 'text-green-700 bg-green-50 border-green-300 shadow-sm'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                OPEN
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {[6, 7, 8, 9, 10].map((stationNum) => (
                          <div key={stationNum} className="border border-slate-200 rounded p-2">
                            <div className="text-xs font-medium text-slate-600 mb-1">Station {stationNum}</div>
                            <div className="flex flex-col space-y-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({...formData, [`station_${stationNum}_arm`]: 'CLOSED'});
                                }}
                                className={`px-1 py-1 text-xs font-medium rounded border transition-colors ${
                                  (formData[`station_${stationNum}_arm` as keyof typeof formData] || 'CLOSED') === 'CLOSED'
                                    ? 'text-red-700 bg-red-50 border-red-300 shadow-sm'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                CLOSED
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({...formData, [`station_${stationNum}_arm`]: 'OPEN'});
                                }}
                                className={`px-1 py-1 text-xs font-medium rounded border transition-colors ${
                                  (formData[`station_${stationNum}_arm` as keyof typeof formData] || 'CLOSED') === 'OPEN'
                                    ? 'text-green-700 bg-green-50 border-green-300 shadow-sm'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                OPEN
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Manual Weight Control for Infeed */}
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3">Infeed Weight Scale Control</h4>
                    
                    {/* Enable Manual Control */}
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.manual_weight_control}
                          onChange={(e) => setFormData({...formData, manual_weight_control: e.target.checked})}
                          className="h-4 w-4 text-indigo-600 border-slate-300 rounded"
                        />
                        <span className="ml-2 text-sm text-slate-700">Enable Manual Weight Control</span>
                      </label>
                    </div>

                    {formData.manual_weight_control && (
                      <>
                        {/* Weight Preset Buttons with ADC Values */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-slate-700 mb-2">Weight Presets with ADC Values</label>
                          <div className="grid grid-cols-4 gap-3">
                            {[
                              { weight: 0, defaultAdc: 8388608 },   // Zero weight = mid-range ADC
                              { weight: 5, defaultAdc: 9227469 },   // 5 lbs
                              { weight: 10, defaultAdc: 10066329 }, // 10 lbs
                              { weight: 25, defaultAdc: 12582912 }  // 25 lbs
                            ].map(preset => (
                              <div key={preset.weight} className="border border-slate-200 rounded-lg p-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const adcValue = formData[`preset_${preset.weight}_adc`] || preset.defaultAdc;
                                    setFormData({
                                      ...formData,
                                      manual_weight_lbs: preset.weight,
                                      manual_adc_value: adcValue
                                    });
                                  }}
                                  className={`w-full px-3 py-1 text-sm font-medium rounded-md border transition-colors mb-2 ${
                                    formData.manual_weight_lbs === preset.weight
                                      ? 'text-white bg-indigo-600 border-indigo-600'
                                      : 'text-slate-700 bg-white border-slate-300 hover:bg-slate-50'
                                  }`}
                                >
                                  {preset.weight} lbs
                                </button>
                                <input
                                  type="number"
                                  value={formData[`preset_${preset.weight}_adc`] || preset.defaultAdc}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    [`preset_${preset.weight}_adc`]: parseInt(e.target.value) || preset.defaultAdc
                                  })}
                                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded-md"
                                  placeholder="ADC"
                                  min="0"
                                  max="16777215"
                                />
                                <div className="text-xs text-slate-500 mt-1">ADC Value</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Manual Weight Input */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Weight (LBS)
                            </label>
                            <input
                              type="number"
                              value={formData.manual_weight_lbs}
                              onChange={(e) => {
                                const weight = parseFloat(e.target.value) || 0;
                                const adcValue = Math.round((weight / 100) * 16777215);
                                setFormData({
                                  ...formData,
                                  manual_weight_lbs: weight,
                                  manual_adc_value: adcValue
                                });
                              }}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                              min="0"
                              max="100"
                              step="0.1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              ADC Value (Raw Signal)
                            </label>
                            <input
                              type="number"
                              value={formData.manual_adc_value}
                              onChange={(e) => setFormData({...formData, manual_adc_value: parseInt(e.target.value) || 0})}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                              min="0"
                              max="16777215"
                            />
                          </div>
                        </div>

                        {/* Scale Calibration Settings */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Zero Point (ADC)
                            </label>
                            <input
                              type="number"
                              value={formData.scale_zero_adc || 8388608}
                              onChange={(e) => setFormData({...formData, scale_zero_adc: parseInt(e.target.value) || 8388608})}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                              min="0"
                              max="16777215"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Calibration Point (ADC)
                            </label>
                            <input
                              type="number"
                              value={formData.scale_calibration_adc || 16777215}
                              onChange={(e) => setFormData({...formData, scale_calibration_adc: parseInt(e.target.value) || 16777215})}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                              min="0"
                              max="16777215"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Calibration State
                            </label>
                            <select
                              value={formData.scale_calibration_state || 'GOOD'}
                              onChange={(e) => setFormData({...formData, scale_calibration_state: e.target.value})}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                            >
                              <option value="GOOD">GOOD</option>
                              <option value="NEEDS_ZERO">NEEDS ZERO</option>
                              <option value="NEEDS_CAL">NEEDS CALIBRATION</option>
                              <option value="ERROR">ERROR</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Load Cell Voltage (V)
                            </label>
                            <input
                              type="number"
                              value={formData.scale_excitation_voltage || 5.0}
                              onChange={(e) => setFormData({...formData, scale_excitation_voltage: parseFloat(e.target.value) || 5.0})}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                              min="0"
                              max="10"
                              step="0.1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Load Cell Current (mA)
                            </label>
                            <input
                              type="number"
                              value={formData.scale_excitation_current || 20.0}
                              onChange={(e) => setFormData({...formData, scale_excitation_current: parseFloat(e.target.value) || 20.0})}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                              min="0"
                              max="50"
                              step="0.1"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Infeed Calibration Lights and Button */}
                  <div className="p-4 bg-amber-50 rounded-lg mt-4">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3">Infeed Calibration Indicators</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {/* Calibration Lights */}
                      <div>
                        <h5 className="text-xs font-semibold text-slate-900 mb-2">Calibration Status Lights</h5>
                        <div className="space-y-2">
                          {/* Red Light */}
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Red Light</label>
                            <div className="flex space-x-1">
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_red_light: 'OFF'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_red_light === 'OFF'
                                    ? 'text-gray-700 bg-gray-200 border-gray-400'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                OFF
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_red_light: 'ON'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_red_light === 'ON'
                                    ? 'text-red-700 bg-red-200 border-red-400'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                🔴 ON
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_red_light: 'FLASH'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_red_light === 'FLASH'
                                    ? 'text-red-700 bg-red-100 border-red-400 animate-pulse'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                ⚡ FLASH
                              </button>
                            </div>
                          </div>

                          {/* Yellow Light */}
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Yellow Light</label>
                            <div className="flex space-x-1">
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_yellow_light: 'OFF'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_yellow_light === 'OFF'
                                    ? 'text-gray-700 bg-gray-200 border-gray-400'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                OFF
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_yellow_light: 'ON'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_yellow_light === 'ON'
                                    ? 'text-yellow-700 bg-yellow-200 border-yellow-400'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                🟡 ON
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_yellow_light: 'FLASH'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_yellow_light === 'FLASH'
                                    ? 'text-yellow-700 bg-yellow-100 border-yellow-400 animate-pulse'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                ⚡ FLASH
                              </button>
                            </div>
                          </div>

                          {/* Green Light */}
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Green Light</label>
                            <div className="flex space-x-1">
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_green_light: 'OFF'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_green_light === 'OFF'
                                    ? 'text-gray-700 bg-gray-200 border-gray-400'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                OFF
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_green_light: 'ON'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_green_light === 'ON'
                                    ? 'text-green-700 bg-green-200 border-green-400'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                🟢 ON
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_green_light: 'FLASH'})}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  formData.infeed_green_light === 'FLASH'
                                    ? 'text-green-700 bg-green-100 border-green-400 animate-pulse'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                ⚡ FLASH
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Calibration Button */}
                      <div>
                        <h5 className="text-xs font-semibold text-slate-900 mb-2">Calibration Control</h5>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Calibration Button</label>
                            <div className="flex space-x-1">
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_calibration_button: 'OFF'})}
                                className={`flex-1 px-3 py-2 text-sm font-medium rounded border transition-colors ${
                                  formData.infeed_calibration_button === 'OFF'
                                    ? 'text-gray-700 bg-gray-200 border-gray-400'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                OFF
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, infeed_calibration_button: 'PRESSED'})}
                                className={`flex-1 px-3 py-2 text-sm font-medium rounded border transition-colors ${
                                  formData.infeed_calibration_button === 'PRESSED'
                                    ? 'text-blue-700 bg-blue-200 border-blue-400 shadow-inner'
                                    : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                }`}
                              >
                                👆 PRESSED
                              </button>
                            </div>
                          </div>
                          
                          <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800">
                            <strong>Calibration Workflow:</strong>
                            <ol className="mt-1 ml-4 list-decimal">
                              <li>Red + Yellow Flash = Needs calibration</li>
                              <li>Press button with empty scale = Set zero</li>
                              <li>Yellow Flash = Add calibration weight</li>
                              <li>Press button again = Complete calibration</li>
                              <li>Green ON = Calibration complete</li>
                            </ol>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Machine States for Outfeed Hardware - Only show on Machine States tab */}
              {formData.node_type === 2 && activeTab === 'machine' && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-slate-900">Outfeed Machine States</h4>
                  
                  {/* Station Configuration Info */}
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-slate-700">
                      <span className="font-medium">Station Configuration {formData.station_config}:</span> 
                      <span className="ml-2">Controls Stations {getStationRangeName(formData.station_config)}</span>
                    </p>
                  </div>


                  {/* Station Subtabs */}
                  {(() => {
                    const validStations = getValidStationsForConfig(formData.station_config);
                    if (validStations.length === 0) return null;
                    
                    // Reset active station tab if it's not valid for current config
                    if (!validStations.includes(activeStationTab)) {
                      setActiveStationTab(validStations[0]);
                    }

                    return (
                      <div className="space-y-4">
                        {/* Station Tab Navigation */}
                        <div className="border-b border-slate-200">
                          <div className="flex space-x-4">
                            {validStations.map(stationNum => (
                              <button
                                key={stationNum}
                                type="button"
                                onClick={() => setActiveStationTab(stationNum)}
                                className={`pb-2 px-3 text-sm font-medium border-b-2 transition-colors ${
                                  activeStationTab === stationNum
                                    ? 'text-indigo-600 border-indigo-600'
                                    : 'text-slate-500 border-transparent hover:text-slate-700'
                                }`}
                              >
                                Station {stationNum}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Station Settings Content */}
                        <div className="grid grid-cols-2 gap-6">
                          {/* Left Column - Station Lights */}
                          <div className="p-4 bg-green-50 rounded-lg">
                            <h4 className="text-sm font-semibold text-slate-900 mb-3">Station {activeStationTab} - Lights & Controls</h4>
                            <div className="space-y-3">
                              {/* Active Button */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Active Button</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_active_button`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_active_button`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_active_button`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_active_button`] === 'ON'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    ON
                                  </button>
                                </div>
                              </div>
                              
                              {/* Red Light */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Red Light</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_red_light`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_red_light`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_red_light`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_red_light`] === 'ON'
                                        ? 'text-red-700 bg-red-100 border-red-400 shadow-sm shadow-red-500/50'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    🔴 ON
                                  </button>
                                </div>
                              </div>

                              {/* Yellow Light */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Yellow Light</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_yellow_light`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_yellow_light`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_yellow_light`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_yellow_light`] === 'ON'
                                        ? 'text-yellow-700 bg-yellow-100 border-yellow-400 shadow-sm shadow-yellow-500/50'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    🟡 ON
                                  </button>
                                </div>
                              </div>

                              {/* Green Light */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Green Light</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_green_light`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_green_light`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_green_light`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_green_light`] === 'ON'
                                        ? 'text-green-700 bg-green-100 border-green-400 shadow-sm shadow-green-500/50'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    🟢 ON
                                  </button>
                                </div>
                              </div>

                              {/* Call for Infeed Product */}
                              <div>
                                <h5 className="text-xs font-semibold text-slate-900 mb-2 mt-3">Call For Infeed Product</h5>
                                <div className="space-y-2">
                                  <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-1">Button</label>
                                    <div className="flex space-x-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setFormData({...formData, [`station_${activeStationTab}_call_for_infeed_button`]: 'OFF'});
                                        }}
                                        className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                          formData[`station_${activeStationTab}_call_for_infeed_button`] === 'OFF'
                                            ? 'text-gray-700 bg-gray-200 border-gray-400'
                                            : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                        }`}
                                      >
                                        OFF
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setFormData({...formData, [`station_${activeStationTab}_call_for_infeed_button`]: 'ON'});
                                        }}
                                        className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                          formData[`station_${activeStationTab}_call_for_infeed_button`] === 'ON'
                                            ? 'text-gray-700 bg-gray-200 border-gray-400'
                                            : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                        }`}
                                      >
                                        ON
                                      </button>
                                    </div>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-1">Blue Light</label>
                                    <div className="flex space-x-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setFormData({...formData, [`station_${activeStationTab}_call_for_infeed_blue_light`]: 'OFF'});
                                        }}
                                        className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                          formData[`station_${activeStationTab}_call_for_infeed_blue_light`] === 'OFF'
                                            ? 'text-gray-700 bg-gray-200 border-gray-400'
                                            : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                        }`}
                                      >
                                        OFF
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setFormData({...formData, [`station_${activeStationTab}_call_for_infeed_blue_light`]: 'ON'});
                                        }}
                                        className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                          formData[`station_${activeStationTab}_call_for_infeed_blue_light`] === 'ON'
                                            ? 'text-blue-700 bg-blue-100 border-blue-400 shadow-sm shadow-blue-500/50'
                                            : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                        }`}
                                      >
                                        🔵 ON
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right Column - Pocket Doors */}
                          <div className="p-4 bg-yellow-50 rounded-lg">
                            <h4 className="text-sm font-semibold text-slate-900 mb-3">Station {activeStationTab} - Pocket Doors</h4>
                            <div className="space-y-3">
                              {/* Pocket Door 1 */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Pocket Door 1</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_pocket_door_1`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_pocket_door_1`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_pocket_door_1`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_pocket_door_1`] === 'ON'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    ON
                                  </button>
                                </div>
                              </div>
                              
                              {/* Pocket Door 2 */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Pocket Door 2</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_pocket_door_2`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_pocket_door_2`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_pocket_door_2`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_pocket_door_2`] === 'ON'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    ON
                                  </button>
                                </div>
                              </div>

                              {/* Pocket Door 3 */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Pocket Door 3</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_pocket_door_3`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_pocket_door_3`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_pocket_door_3`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_pocket_door_3`] === 'ON'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    ON
                                  </button>
                                </div>
                              </div>

                              {/* Pocket Door 4 */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Pocket Door 4</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_pocket_door_4`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_pocket_door_4`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_pocket_door_4`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_pocket_door_4`] === 'ON'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    ON
                                  </button>
                                </div>
                              </div>

                              {/* Weigh Hopper Door */}
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Weigh Hopper Door</label>
                                <div className="flex space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_weigh_hopper_door`]: 'OFF'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_weigh_hopper_door`] === 'OFF'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    OFF
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({...formData, [`station_${activeStationTab}_weigh_hopper_door`]: 'ON'});
                                    }}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      formData[`station_${activeStationTab}_weigh_hopper_door`] === 'ON'
                                        ? 'text-gray-700 bg-gray-200 border-gray-400'
                                        : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'
                                    }`}
                                  >
                                    ON
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Manual Weight Control for Outfeed Station */}
                          <div className="mt-6 p-4 bg-purple-50 rounded-lg col-span-2">
                            <h4 className="text-sm font-semibold text-slate-900 mb-3">Station {activeStationTab} - Manual Weight Scale Control</h4>
                            
                            {/* Enable Manual Control */}
                            <div className="mb-4">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.manual_weight_control}
                                  onChange={(e) => setFormData({...formData, manual_weight_control: e.target.checked})}
                                  className="h-4 w-4 text-indigo-600 border-slate-300 rounded"
                                />
                                <span className="ml-2 text-sm text-slate-700">Enable Manual Weight Control</span>
                              </label>
                            </div>

                            {formData.manual_weight_control && (
                              <>
                                {/* Weight Preset Buttons with ADC Values */}
                                <div className="mb-4">
                                  <label className="block text-sm font-medium text-slate-700 mb-2">Weight Presets with ADC Values</label>
                                  <div className="grid grid-cols-4 gap-3">
                                    {[
                                      { weight: 0, defaultAdc: 8388608 },   // Zero weight = mid-range ADC
                                      { weight: 5, defaultAdc: 9227469 },   // 5 lbs
                                      { weight: 10, defaultAdc: 10066329 }, // 10 lbs
                                      { weight: 25, defaultAdc: 12582912 }  // 25 lbs
                                    ].map(preset => (
                                      <div key={preset.weight} className="border border-slate-200 rounded-lg p-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const adcValue = formData[`station_${activeStationTab}_preset_${preset.weight}_adc`] || preset.defaultAdc;
                                            setFormData({
                                              ...formData,
                                              [`station_${activeStationTab}_manual_weight_lbs`]: preset.weight,
                                              [`station_${activeStationTab}_manual_adc_value`]: adcValue
                                            });
                                          }}
                                          className={`w-full px-3 py-1 text-sm font-medium rounded-md border transition-colors mb-2 ${
                                            formData[`station_${activeStationTab}_manual_weight_lbs`] === preset.weight
                                              ? 'text-white bg-indigo-600 border-indigo-600'
                                              : 'text-slate-700 bg-white border-slate-300 hover:bg-slate-50'
                                          }`}
                                        >
                                          {preset.weight} lbs
                                        </button>
                                        <input
                                          type="number"
                                          value={formData[`station_${activeStationTab}_preset_${preset.weight}_adc`] || preset.defaultAdc}
                                          onChange={(e) => setFormData({
                                            ...formData,
                                            [`station_${activeStationTab}_preset_${preset.weight}_adc`]: parseInt(e.target.value) || preset.defaultAdc
                                          })}
                                          className="w-full px-2 py-1 text-xs border border-slate-300 rounded-md"
                                          placeholder="ADC"
                                          min="0"
                                          max="16777215"
                                        />
                                        <div className="text-xs text-slate-500 mt-1">ADC Value</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Manual Weight Input */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                      Weight (LBS)
                                    </label>
                                    <input
                                      type="number"
                                      value={formData[`station_${activeStationTab}_manual_weight_lbs`] || 0}
                                      onChange={(e) => {
                                        const weight = parseFloat(e.target.value) || 0;
                                        const adcValue = Math.round((weight / 100) * 16777215);
                                        setFormData({
                                          ...formData,
                                          [`station_${activeStationTab}_manual_weight_lbs`]: weight,
                                          [`station_${activeStationTab}_manual_adc_value`]: adcValue
                                        });
                                      }}
                                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                      ADC Value (Raw Signal)
                                    </label>
                                    <input
                                      type="number"
                                      value={formData[`station_${activeStationTab}_manual_adc_value`] || 8388608}
                                      onChange={(e) => setFormData({...formData, [`station_${activeStationTab}_manual_adc_value`]: parseInt(e.target.value) || 0})}
                                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                                      min="0"
                                      max="16777215"
                                    />
                                  </div>
                                </div>

                                {/* Scale Calibration Settings */}
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                      Zero Point (ADC)
                                    </label>
                                    <input
                                      type="number"
                                      value={formData[`station_${activeStationTab}_scale_zero_adc`] || 8388608}
                                      onChange={(e) => setFormData({...formData, [`station_${activeStationTab}_scale_zero_adc`]: parseInt(e.target.value) || 8388608})}
                                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                                      min="0"
                                      max="16777215"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                      Calibration Point (ADC)
                                    </label>
                                    <input
                                      type="number"
                                      value={formData[`station_${activeStationTab}_scale_calibration_adc`] || 16777215}
                                      onChange={(e) => setFormData({...formData, [`station_${activeStationTab}_scale_calibration_adc`]: parseInt(e.target.value) || 16777215})}
                                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                                      min="0"
                                      max="16777215"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                      Calibration State
                                    </label>
                                    <select
                                      value={formData[`station_${activeStationTab}_scale_calibration_state`] || 'GOOD'}
                                      onChange={(e) => setFormData({...formData, [`station_${activeStationTab}_scale_calibration_state`]: e.target.value})}
                                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                                    >
                                      <option value="GOOD">GOOD</option>
                                      <option value="NEEDS_ZERO">NEEDS ZERO</option>
                                      <option value="NEEDS_CAL">NEEDS CALIBRATION</option>
                                      <option value="ERROR">ERROR</option>
                                    </select>
                                  </div>
                                </div>

                                {/* Load Cell Settings */}
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                      Load Cell Voltage (V)
                                    </label>
                                    <input
                                      type="number"
                                      value={formData[`station_${activeStationTab}_scale_excitation_voltage`] || 5.0}
                                      onChange={(e) => setFormData({...formData, [`station_${activeStationTab}_scale_excitation_voltage`]: parseFloat(e.target.value) || 5.0})}
                                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                                      min="0"
                                      max="10"
                                      step="0.1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                      Load Cell Current (mA)
                                    </label>
                                    <input
                                      type="number"
                                      value={formData[`station_${activeStationTab}_scale_excitation_current`] || 20.0}
                                      onChange={(e) => setFormData({...formData, [`station_${activeStationTab}_scale_excitation_current`]: parseFloat(e.target.value) || 20.0})}
                                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                                      min="0"
                                      max="50"
                                      step="0.1"
                                    />
                                  </div>
                                </div>

                                <p className="mt-3 text-xs text-slate-600">
                                  Configure manual weight and calibration settings for Station {activeStationTab}. Each station has independent settings.
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {/* Test Logging Tab */}
              {activeTab === 'logging' && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-slate-900">Generate Test MQTT Logs</h4>
                  <p className="text-sm text-slate-600">
                    Send test log messages via MQTT to simulate real hardware logging.
                    These logs will be processed by the MQTT consumer and stored in the database.
                  </p>

                  <div className="grid grid-cols-5 gap-4">
                    {['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'].map(level => (
                      <div key={level} className="space-y-2">
                        <div className={`font-semibold text-center py-2 rounded ${
                          level === 'DEBUG' ? 'text-gray-600 bg-gray-100' :
                          level === 'INFO' ? 'text-blue-600 bg-blue-100' :
                          level === 'WARNING' ? 'text-yellow-600 bg-yellow-100' :
                          level === 'ERROR' ? 'text-orange-600 bg-orange-100' :
                          'text-red-600 bg-red-100'
                        }`}>
                          {level}
                        </div>
                        {['SYSTEM', 'CALIBRATION', 'SENSOR', 'NETWORK', 'MECHANICAL'].map(type => (
                          <button
                            key={type}
                            onClick={() => generateTestLog(selectedDevice!, level, type)}
                            className="w-full px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50 hover:border-slate-400 transition-colors"
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2">How it works:</h5>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Sends MQTT DLOG message to simulate real hardware logging</li>
                      <li>• Message processed by MQTT Sparkplug consumer</li>
                      <li>• Log stored in hardware_logs database table</li>
                      <li>• View results in Hardware → Hardware Logging page</li>
                      <li>• Real-time WebSocket updates notify UI of new logs</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Station Status Tab - Only for Infeed devices */}
              {activeTab === 'stations' && (formData.node_type === 1 || formData.node_type === '1') && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-slate-900">Station Status (Hardware View)</h4>
                  <p className="text-sm text-slate-600">
                    This shows what the hardware knows from MQTT messages - station numbers only, no employee info:
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4">
                    {stationAssignments.length > 0 ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-4 text-sm font-medium text-gray-700 pb-2 border-b border-gray-200">
                          <div>Station Number</div>
                          <div>Status</div>
                        </div>
                        {stationAssignments.map((assignment: any, index: number) => (
                          <div key={index} className="grid grid-cols-2 gap-4 text-sm py-2 border-b border-gray-100">
                            <div className="font-medium">Station {assignment.stationNumber}</div>
                            <div>
                              <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                                assignment.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {assignment.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p className="mb-2">No active stations reported by hardware for Line {formData.line_number}.</p>
                        <p className="text-sm">Hardware may not have received station status yet via MQTT.</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2">Station Configuration:</h5>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Active stations receive infeed material during production</li>
                      <li>• Inactive stations do not receive material</li>
                      <li>• Station assignments are managed via the Production → Stations page</li>
                      <li>• The infeed simulator uses this information to determine weight distribution</li>
                      <li>• Station status is synchronized via MQTT from the backend</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Simulation Configuration Tab */}
              {activeTab === 'simulation' && (
                <SimulationConfigTab
                  device={selectedDevice}
                  lineNumber={selectedDevice?.line_number || 1}
                  stationNumber={selectedDevice?.node_type === 1 ? 0 : selectedDevice?.station_config || 1}
                  isInfeed={selectedDevice?.node_type === 1}
                  onConfigUpdate={() => {
                    // Refresh device list after configuration update
                    fetchDevices();
                  }}
                />
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end space-x-3 flex-shrink-0">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDevice}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
              >
                {isEditing ? 'Update Device' : 'Create Device'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MQTT Configuration Modal */}
      {showConfigModal && configDevice && (
        <MqttConfigModal 
          device={configDevice}
          onClose={() => {
            setShowConfigModal(false);
            setConfigDevice(null);
          }}
        />
      )}
    </div>
  );
};

export default HardwareSimulation;